import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "login-page" }
const _hoisted_2 = { class: "w-100" }
const _hoisted_3 = { class: "text-subtitle-1 mb-0" }
const _hoisted_4 = {
  key: 0,
  class: "signup-success"
}

import { ref, computed, onMounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import { useSchools } from '@/composables/useSchools'
import axios from 'axios'


export default /*@__PURE__*/_defineComponent({
  __name: 'LoginView',
  setup(__props) {

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { schools, loadSchools } = useSchools()

const activeTab = ref('login')
const loginFormRef = ref<any>(null)
const signupFormRef = ref<any>(null)

// Load schools on mount and check for signup query param
onMounted(() => {
  loadSchools()
  loadAllowedDomains()

  // Check if signup query parameter is present
  if (route.query.signup === 'true' || route.query.signup === '1') {
    activeTab.value = 'signup'
  }
})

const credentials = ref({
  username: '',
  password: ''
})

const signupData = ref({
  first_name: '',
  last_name: '',
  email: '',
  school: '',
  password: '',
  password_confirm: ''
})

const loading = ref(false)
const error = ref('')
const successMessage = ref('')
const signupSuccess = ref(false)
const allowedDomains = ref<string[]>([])

// Load allowed domains from configuration
const loadAllowedDomains = async () => {
  try {
    const response = await axios.get('/api/v1/configuration')
    allowedDomains.value = response.data.allowed_domains || []
  } catch (err) {
    console.error('Error loading allowed domains:', err)
    allowedDomains.value = []
  }
}

// Email domain validation
const validateEmailDomain = (email: string): boolean | string => {
  if (allowedDomains.value.length === 0) {
    return true // No restrictions if no domains configured
  }
  const emailLower = email.toLowerCase()
  for (const domain of allowedDomains.value) {
    if (emailLower.endsWith('@' + domain.toLowerCase())) {
      return true
    }
  }
  const domainList = allowedDomains.value.map(d => '@' + d).join(', ')
  return `Email must end with: ${domainList}`
}

// Hint text for email field
const emailHint = computed(() => {
  if (allowedDomains.value.length === 0) {
    return ''
  }
  if (allowedDomains.value.length === 1) {
    return `Must be a @${allowedDomains.value[0]} email address`
  }
  return `Allowed domains: ${allowedDomains.value.map(d => '@' + d).join(', ')}`
})

const schoolOptions = computed(() => {
  const options = schools.value.map(school => ({
    title: school.name,
    value: school.id
  }))
  return options
})

const resetSignupForm = () => {
  signupSuccess.value = false
  activeTab.value = 'login'
  signupData.value = {
    first_name: '',
    last_name: '',
    email: '',
    school: '',
    password: '',
    password_confirm: ''
  }
  signupFormRef.value?.reset()
}

const handleLogin = async () => {
  const { valid } = await loginFormRef.value.validate()
  if (!valid) return

  loading.value = true
  error.value = ''

  try {
    const response = await authStore.login(credentials.value)

    if (response.status === 200) {
      // Successfully logged in, redirect to portal
      router.push('/')
    } else {
      error.value = 'Invalid username or password'
    }
  } catch (err: any) {
    console.error('Login error:', err)
    if (err.response?.status === 401) {
      error.value = 'Invalid username or password'
    } else if (err.response?.status === 403) {
      error.value = 'Your account is pending approval. Please wait for an administrator to approve your account.'
    } else {
      error.value = 'An error occurred. Please try again.'
    }
  } finally {
    loading.value = false
  }
}

const handleSignup = async () => {
  const { valid } = await signupFormRef.value.validate()
  if (!valid) return

  loading.value = true
  error.value = ''
  successMessage.value = ''

  // Validate passwords match
  if (signupData.value.password !== signupData.value.password_confirm) {
    error.value = 'Passwords do not match'
    loading.value = false
    return
  }

  try {
    const response = await axios.post('/api/v1/auth/signup', signupData.value)

    if (response.data.error) {
      error.value = response.data.error
    } else {
      // Show success state instead of alert
      signupSuccess.value = true
      error.value = ''
    }
  } catch (err: any) {
    console.error('Signup error:', err)
    error.value = err.response?.data?.error || 'An error occurred during signup. Please try again.'
  } finally {
    loading.value = false
  }
}

return (_ctx: any,_cache: any) => {
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_form = _resolveComponent("v-form")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_alert_title = _resolveComponent("v-alert-title")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_divider = _resolveComponent("v-divider")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_v_container, { class: "fill-height" }, {
      default: _withCtx(() => [
        _createVNode(_component_v_row, {
          align: "center",
          justify: "center"
        }, {
          default: _withCtx(() => [
            _createVNode(_component_v_col, {
              cols: "12",
              sm: "10",
              md: "8",
              lg: "6",
              xl: "4"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_card, {
                  elevation: "24",
                  class: "login-card"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_card_title, { class: "login-header text-center pa-8" }, {
                      default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_2, [
                          _cache[10] || (_cache[10] = _createElementVNode("img", {
                            src: "/assets/img/logo-trans.png",
                            alt: "Space Coast STEM",
                            class: "logo mb-4"
                          }, null, -1)),
                          _cache[11] || (_cache[11] = _createElementVNode("h1", { class: "text-h4 font-weight-bold mb-2" }, "Portal Access", -1)),
                          _createElementVNode("p", _hoisted_3, _toDisplayString(activeTab.value === 'login' ? 'Sign in to access the dashboard' : 'Create a new account'), 1)
                        ])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_tabs, {
                      modelValue: activeTab.value,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((activeTab).value = $event)),
                      "bg-color": "grey-lighten-4",
                      grow: ""
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_tab, { value: "login" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, { start: "" }, {
                              default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                                _createTextVNode("mdi-login", -1)
                              ]))]),
                              _: 1
                            }),
                            _cache[13] || (_cache[13] = _createTextVNode(" Login ", -1))
                          ]),
                          _: 1
                        }),
                        _createVNode(_component_v_tab, { value: "signup" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, { start: "" }, {
                              default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                                _createTextVNode("mdi-account-plus", -1)
                              ]))]),
                              _: 1
                            }),
                            _cache[15] || (_cache[15] = _createTextVNode(" Sign Up ", -1))
                          ]),
                          _: 1
                        })
                      ]),
                      _: 1
                    }, 8, ["modelValue"]),
                    _createVNode(_component_v_card_text, { class: "pt-8" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_v_window, {
                          modelValue: activeTab.value,
                          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((activeTab).value = $event)),
                          class: "pa-4"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_window_item, { value: "login" }, {
                              default: _withCtx(() => [
                                _createVNode(_component_v_form, {
                                  onSubmit: _withModifiers(handleLogin, ["prevent"]),
                                  ref_key: "loginFormRef",
                                  ref: loginFormRef
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_text_field, {
                                      modelValue: credentials.value.username,
                                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((credentials.value.username) = $event)),
                                      label: "Email or Username",
                                      variant: "outlined",
                                      "prepend-inner-icon": "mdi-account",
                                      rules: [v => !!v || 'Username is required'],
                                      required: "",
                                      class: "mb-3"
                                    }, null, 8, ["modelValue", "rules"]),
                                    _createVNode(_component_v_text_field, {
                                      modelValue: credentials.value.password,
                                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((credentials.value.password) = $event)),
                                      label: "Password",
                                      type: "password",
                                      variant: "outlined",
                                      "prepend-inner-icon": "mdi-lock",
                                      rules: [v => !!v || 'Password is required'],
                                      required: "",
                                      class: "mb-3"
                                    }, null, 8, ["modelValue", "rules"]),
                                    (error.value)
                                      ? (_openBlock(), _createBlock(_component_v_alert, {
                                          key: 0,
                                          type: "error",
                                          variant: "tonal",
                                          class: "mb-4"
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(error.value), 1)
                                          ]),
                                          _: 1
                                        }))
                                      : _createCommentVNode("", true),
                                    _createVNode(_component_v_btn, {
                                      type: "submit",
                                      color: "primary",
                                      size: "large",
                                      block: "",
                                      loading: loading.value,
                                      class: "text-none"
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_icon, { start: "" }, {
                                          default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                            _createTextVNode("mdi-login", -1)
                                          ]))]),
                                          _: 1
                                        }),
                                        _cache[17] || (_cache[17] = _createTextVNode(" Sign In ", -1))
                                      ]),
                                      _: 1
                                    }, 8, ["loading"])
                                  ]),
                                  _: 1
                                }, 512)
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_window_item, { value: "signup" }, {
                              default: _withCtx(() => [
                                (signupSuccess.value)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                      _createVNode(_component_v_icon, {
                                        size: "80",
                                        color: "success",
                                        class: "mb-4"
                                      }, {
                                        default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                                          _createTextVNode("mdi-check-circle", -1)
                                        ]))]),
                                        _: 1
                                      }),
                                      _cache[27] || (_cache[27] = _createElementVNode("h2", { class: "text-h5 mb-4" }, "Account Created Successfully!", -1)),
                                      _createVNode(_component_v_alert, {
                                        type: "warning",
                                        class: "mb-4 text-left"
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_alert_title, { class: "mb-3" }, {
                                            default: _withCtx(() => [
                                              _createVNode(_component_v_icon, { start: "" }, {
                                                default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                                                  _createTextVNode("mdi-clock-alert", -1)
                                                ]))]),
                                                _: 1
                                              }),
                                              _cache[20] || (_cache[20] = _createElementVNode("strong", null, "Approval Required", -1))
                                            ]),
                                            _: 1
                                          }),
                                          _cache[21] || (_cache[21] = _createElementVNode("p", { class: "mb-2" }, "Your account is pending administrator approval. You will not be able to log in until your account has been approved.", -1)),
                                          _cache[22] || (_cache[22] = _createElementVNode("p", { class: "mb-3" }, [
                                            _createElementVNode("strong", null, "What happens next?")
                                          ], -1)),
                                          _cache[23] || (_cache[23] = _createElementVNode("ul", { class: "mb-2" }, [
                                            _createElementVNode("li", null, "Our administrators have been notified of your signup"),
                                            _createElementVNode("li", null, "They will review your account information"),
                                            _createElementVNode("li", null, "You'll receive an email when your account is approved"),
                                            _createElementVNode("li", null, "Once approved, you can log in and start using the portal")
                                          ], -1)),
                                          _cache[24] || (_cache[24] = _createElementVNode("p", { class: "mb-0 text-caption" }, "This typically happens within 24 hours.", -1))
                                        ]),
                                        _: 1
                                      }),
                                      _createVNode(_component_v_btn, {
                                        color: "primary",
                                        size: "large",
                                        onClick: resetSignupForm,
                                        class: "text-none"
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_icon, { start: "" }, {
                                            default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                                              _createTextVNode("mdi-arrow-left", -1)
                                            ]))]),
                                            _: 1
                                          }),
                                          _cache[26] || (_cache[26] = _createTextVNode(" Back to Login ", -1))
                                        ]),
                                        _: 1
                                      })
                                    ]))
                                  : (_openBlock(), _createBlock(_component_v_form, {
                                      key: 1,
                                      onSubmit: _withModifiers(handleSignup, ["prevent"]),
                                      ref_key: "signupFormRef",
                                      ref: signupFormRef
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(_component_v_row, null, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_col, {
                                              cols: "12",
                                              sm: "6"
                                            }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_v_text_field, {
                                                  modelValue: signupData.value.first_name,
                                                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((signupData.value.first_name) = $event)),
                                                  label: "First Name",
                                                  variant: "outlined",
                                                  rules: [v => !!v || 'First name is required'],
                                                  required: ""
                                                }, null, 8, ["modelValue", "rules"])
                                              ]),
                                              _: 1
                                            }),
                                            _createVNode(_component_v_col, {
                                              cols: "12",
                                              sm: "6"
                                            }, {
                                              default: _withCtx(() => [
                                                _createVNode(_component_v_text_field, {
                                                  modelValue: signupData.value.last_name,
                                                  "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((signupData.value.last_name) = $event)),
                                                  label: "Last Name",
                                                  variant: "outlined",
                                                  rules: [v => !!v || 'Last name is required'],
                                                  required: ""
                                                }, null, 8, ["modelValue", "rules"])
                                              ]),
                                              _: 1
                                            })
                                          ]),
                                          _: 1
                                        }),
                                        _createVNode(_component_v_text_field, {
                                          modelValue: signupData.value.email,
                                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((signupData.value.email) = $event)),
                                          label: "Email",
                                          type: "email",
                                          variant: "outlined",
                                          "prepend-inner-icon": "mdi-email",
                                          rules: [
                        v => !!v || 'Email is required',
                        v => /.+@.+\..+/.test(v) || 'Email must be valid',
                        validateEmailDomain
                      ],
                                          required: "",
                                          class: "mb-3",
                                          hint: emailHint.value,
                                          "persistent-hint": !!emailHint.value
                                        }, null, 8, ["modelValue", "rules", "hint", "persistent-hint"]),
                                        _createVNode(_component_v_select, {
                                          modelValue: signupData.value.school,
                                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((signupData.value.school) = $event)),
                                          items: schoolOptions.value,
                                          label: "School",
                                          variant: "outlined",
                                          "prepend-inner-icon": "mdi-school",
                                          rules: [v => !!v || 'School is required'],
                                          required: "",
                                          class: "mb-3"
                                        }, null, 8, ["modelValue", "items", "rules"]),
                                        _createVNode(_component_v_text_field, {
                                          modelValue: signupData.value.password,
                                          "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((signupData.value.password) = $event)),
                                          label: "Password",
                                          type: "password",
                                          variant: "outlined",
                                          "prepend-inner-icon": "mdi-lock",
                                          rules: [
                        v => !!v || 'Password is required',
                        v => v.length >= 8 || 'Password must be at least 8 characters'
                      ],
                                          required: "",
                                          class: "mb-3"
                                        }, null, 8, ["modelValue", "rules"]),
                                        _createVNode(_component_v_text_field, {
                                          modelValue: signupData.value.password_confirm,
                                          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((signupData.value.password_confirm) = $event)),
                                          label: "Confirm Password",
                                          type: "password",
                                          variant: "outlined",
                                          "prepend-inner-icon": "mdi-lock-check",
                                          rules: [
                        v => !!v || 'Please confirm password',
                        v => v === signupData.value.password || 'Passwords must match'
                      ],
                                          required: "",
                                          class: "mb-3"
                                        }, null, 8, ["modelValue", "rules"]),
                                        (error.value)
                                          ? (_openBlock(), _createBlock(_component_v_alert, {
                                              key: 0,
                                              type: "error",
                                              variant: "tonal",
                                              class: "mb-4"
                                            }, {
                                              default: _withCtx(() => [
                                                _createTextVNode(_toDisplayString(error.value), 1)
                                              ]),
                                              _: 1
                                            }))
                                          : _createCommentVNode("", true),
                                        _createVNode(_component_v_btn, {
                                          type: "submit",
                                          color: "primary",
                                          size: "large",
                                          block: "",
                                          loading: loading.value,
                                          class: "text-none"
                                        }, {
                                          default: _withCtx(() => [
                                            _createVNode(_component_v_icon, { start: "" }, {
                                              default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                                                _createTextVNode("mdi-account-plus", -1)
                                              ]))]),
                                              _: 1
                                            }),
                                            _cache[29] || (_cache[29] = _createTextVNode(" Create Account ", -1))
                                          ]),
                                          _: 1
                                        }, 8, ["loading"])
                                      ]),
                                      _: 1
                                    }, 512))
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["modelValue"])
                      ]),
                      _: 1
                    }),
                    _createVNode(_component_v_divider)
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})